import React from 'react';
import { useTranslation } from 'react-i18next';
import { Point } from 'highcharts';
import ReactDOMServer from 'react-dom/server';
import styles2 from './style.module.scss';
import { FormatNumberForChart } from '@components-clean/core/charts/shared/number-utils';
import styles from '@components-clean/core/charts/shared/style.module.scss';
import { RAGColourIndex, RAGLabel } from '@components-clean/core/charts/shared/utils';
import PanelFrame from '@components-clean/core/panel-frame';
import { LineSeriesDataProperties } from '@interfaces-clean/panels.interface';
import { ExtendedCompleteRiskFieldsEvaluationHistory } from '@pages-clean/risk-assessment/risk-log/constants';
import { PanelSeriesType, PanelType, RiskEvaluationColor } from 'types/graphql-global-types';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';

interface EvaluationChartProps {
    evaluations: ExtendedCompleteRiskFieldsEvaluationHistory[];
}

const EvaluationsChart = ({ evaluations }: EvaluationChartProps) => {
    const { t } = useTranslation('ram', { keyPrefix: 'riskLog' });
    const startPos = evaluations.length < 12 ? 0 : evaluations.length - 12;

    const xAxisCategories: string[] = [];
    const chartData = [
        evaluations
            .reverse()
            .slice(startPos, evaluations.length)
            .map((evaluation) => {
                const { colour, score } = evaluation.score;
                const created = new Date(evaluation.created).toISOString();
                xAxisCategories.push(created);
                return {
                    x: created,
                    y: score,
                    color: RAGColourIndex(RiskEvaluationColor[colour ?? 'NONE']),
                    score,
                    evaluationModel: evaluation.evaluationModelName,
                };
            }),
    ];

    return (
        <PanelFrame
            title={t('tabs.evaluation.chart.title')}
            data-testid="ram-evaluaitons-spark-line"
            panelType={PanelType.GRAPH}
            seriesConfig={{
                type: PanelSeriesType.SITE,
                graphSeries: [{ label: t('grids.evaluations.title') }],
            }}
            graphType="SPARKLINE"
            showLegend={false}
            disablePanelSetup
            hidePanelSetup
            xAxisTitle=""
            className={styles2.evaluationChart}
            yAxisTitle=""
            showxAxisLabels={false}
            panelData={{
                categories: [...xAxisCategories],
                sparkLineData: chartData,
            }}
            customOptions={{
                yAxis: {
                    tickPixelInterval: 20,
                },
                plotOptions: {
                    series: {
                        color: styles['primary-line-color'],
                        states: {
                            hover: {
                                lineWidthPlus: 0,
                            },
                        },
                    },
                },
                tooltip: {
                    formatter() {
                        const point = this as Point;
                        const customProps = point.options as LineSeriesDataProperties;
                        return ReactDOMServer.renderToStaticMarkup(
                            <div>
                                <br />
                                <b>{t('tabs.evaluation.chart.tooltipLabels.score')}: </b>
                                {FormatNumberForChart(customProps.score)}
                                <br />
                                <b>{t('tabs.evaluation.chart.tooltipLabels.rag')}:</b>{' '}
                                {RAGLabel(customProps.colorIndex as number)}
                                <br />
                                <b>{t('tabs.evaluation.chart.tooltipLabels.date')}:</b>{' '}
                                {stringDateWithoutTimeFromUtc(new Date(point.category), 'DD-MMM-YYYY')}
                                <br />
                                <b>{t('tabs.evaluation.chart.tooltipLabels.evaluationModel')}:</b>{' '}
                                {'evaluationModel' in customProps && customProps.evaluationModel}
                            </div>,
                        );
                    },
                },
                chart: {
                    height: null,
                },
            }}
            version={0}
        />
    );
};

export default EvaluationsChart;
